import i18n from '@/utils/i18n';
import React from 'react';
import { ActivityIndicator } from 'react-native';
import { ActivityNote } from '@/experiences/sales-portal/components/activity-notes/types';
import {
  Badge,
  Icons,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { DetailSectionEmptyState } from '@/experiences/sales-portal/components/detail-section-empty-state';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import {
  formatNoteMetadata,
  formatNoteReminder,
  formatVisitPills,
  NOTE_MAX_LINES,
  useActivityNoteStyles,
} from '@/experiences/sales-portal/components/activity-notes/utils';

interface ActivityNotesDesktopProps {
  activityNotes: ActivityNote[];
  hasError: boolean;
  loading: boolean;
  onRetry: () => void;
}

export const ActivityNotesDesktop: React.FC<ActivityNotesDesktopProps> = ({
  activityNotes,
  hasError,
  loading,
  onRetry,
}) => {
  const [styles] = useActivityNoteStyles();

  return (
    <YStack>
      <YStack style={[styles.sectionHeader, styles.sectionHeaderDesktop]}>
        <Typography
          accessibilityRole="header"
          bold
          size="$md"
          variant="heading"
        >
          {i18n.t('sales_portal.activity_notes.title')}
        </Typography>
      </YStack>
      {hasError && activityNotes.length === 0 ? (
        <ErrorState
          description={i18n.t('sales_portal.activity_notes.error')}
          onPress={onRetry}
          title={i18n.t('sales_portal.error.title')}
        />
      ) : activityNotes.length === 0 ? (
        <DetailSectionEmptyState
          text={i18n.t('sales_portal.activity_notes.empty_state')}
        />
      ) : (
        <YStack style={styles.notesList}>
          {activityNotes.map((note) => {
            const reminder = formatNoteReminder(note);
            const pills = formatVisitPills(note);

            return (
              <YStack key={note.uuid} style={styles.card}>
                <Typography numberOfLines={NOTE_MAX_LINES} size="$sm">
                  {note.content}
                </Typography>
                <XStack style={styles.metadataRow}>
                  <Typography variant="caption">
                    {formatNoteMetadata(note)}
                  </Typography>
                  {reminder ? (
                    <XStack accessibilityLabel={reminder.accessibilityLabel}>
                      <Badge color="info" icon={<Icons.Bell />}>
                        {reminder.text}
                      </Badge>
                    </XStack>
                  ) : null}
                  {pills.map((pill) => (
                    <Badge color="muted" key={pill.key}>
                      {pill.label}
                    </Badge>
                  ))}
                </XStack>
              </YStack>
            );
          })}
          {loading ? (
            <ActivityIndicator style={styles.loadingIndicator} />
          ) : null}
        </YStack>
      )}
    </YStack>
  );
};
